import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Formik, FormikHelpers } from 'formik';
import { validateEmail, validateEmailList, validateRequired } from 'utils/validation';
import { errorMessageEmailInvalid, errorMessageEmailListInvalid, errorMessageFileType } from 'constants/validation';
import { formatErrors } from 'utils/helpers';
import { colourGroups, colours } from 'constants/theme';
import { ISettingsValues } from 'types';
import { Button } from 'components/UI';
import ErrorPanel from 'components/common/ErrorPanel';
import SettingsInfo from '../SettingsInfo';

type SettingsFormValues = ISettingsValues | Record<string, never>;

export interface ISection {
  id: string;
  text: string;
  description: string;
  datatable?: boolean;
  icon: React.ElementType;
}

export type EmailErrors = {
  adminEmail?: string;
  adminRfqEmail?: string;
};

export type FileErrors = {
  headerLogo?: string;
  loginBackgroundImage?: string;
  emailLogo?: string;
  loginLogo?: string;
};

type SettingsFormProps = {
  onSubmit: (values: SettingsFormValues, helpers: FormikHelpers<SettingsFormValues>) => Promise<void>;
  initialValues?: SettingsFormValues;
  formComponent: React.ElementType;
  section?: ISection;
  countries?: { name: string; iso2: string }[];
};

const validateEmailListField = (values: SettingsFormValues, errors: Record<string, string>, key: keyof EmailErrors) => {
  const emailListErrors: EmailErrors = {};

  if (!values[key]) return {};

  if (!validateEmail(values[key]) && !values[key].includes(',')) {
    emailListErrors[key] = errorMessageEmailInvalid;
  } else if (values[key].includes(',') && !validateEmailList(values[key])) {
    emailListErrors[key] = errorMessageEmailListInvalid;
  }

  return { ...errors, ...emailListErrors };
};

const validateFileField = (values: SettingsFormValues, errors: Record<string, string>, key: keyof FileErrors) => {
  if (!values[key]) return errors;

  const fileErrors: FileErrors = {};

  if (typeof values[key] === 'string' || !values[key]) return errors;

  if (!(values[key] as File[]).every((file) => file.type.includes('image'))) {
    fileErrors[key] = errorMessageFileType;
  }

  return { ...errors, ...fileErrors };
};

const SettingsForm = ({
  onSubmit,
  initialValues = {},
  formComponent: FormComponent,
  section,
  countries,
}: SettingsFormProps) => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <Formik
      initialValues={initialValues}
      validate={async (values) => {
        const errors = validateRequired<SettingsFormValues>(
          [
            ...colours,
            'countries',
            'adminEmail',
            'adminRfqEmail',
            'companyName',
            'headerLogo',
            'loginLogo',
            'emailLogo',
          ],
          values
        );

        const adminEmailErrors = validateEmailListField(values, errors, 'adminEmail');

        const adminRFQEmailErrors = validateEmailListField(values, errors, 'adminRfqEmail');

        const headerLogoErrors = validateFileField(values, errors, 'headerLogo');

        const backgroundImageErrors = values.loginBackgroundImage
          ? validateFileField(values, errors, 'loginBackgroundImage')
          : {};

        const emailLogoErrors = validateFileField(values, errors, 'emailLogo');

        const loginLogoErrors = validateFileField(values, errors, 'loginLogo');

        return {
          ...errors,
          ...adminEmailErrors,
          ...adminRFQEmailErrors,
          ...headerLogoErrors,
          ...loginLogoErrors,
          ...emailLogoErrors,
          ...backgroundImageErrors,
        };
      }}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => {
        const renderErrors =
          submitted && errors && Object.keys(errors).length > 0 ? (
            <ErrorPanel
              title="The following errors occurred during submission:"
              errors={formatErrors(errors as Record<string, string>)}
            />
          ) : null;

        const renderInfo = section ? (
          <SettingsInfo title={section.text} description={section.description} icon={section.icon} />
        ) : null;

        const renderSaveButton = !section?.datatable ? (
          <Button
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={() => setSubmitted(true)}
            type="submit"
            data-testid="submit"
          >
            SAVE
          </Button>
        ) : null;

        const overflowStyles = {
          overflow: section?.datatable ? 'visible' : 'hidden',
        };

        return (
          <Flex
            data-testid="settings-form"
            sx={{
              flexDir: 'column',
              flex: '1 1',
              bgColor: 'base2Colour.400',
              p: 3,
              ...overflowStyles,
            }}
          >
            {renderErrors}
            {renderInfo}

            <form
              style={{
                width: '100%',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                ...overflowStyles,
              }}
              onSubmit={handleSubmit}
            >
              <Flex flex={1} flexDir="column" bgColor="base2Colour.500" mb={4} {...overflowStyles}>
                <Box pr={6} pb={6} m={6} overflowY={section?.datatable ? 'visible' : 'auto'} flex={1}>
                  <FormComponent
                    values={values}
                    submitted={submitted}
                    errors={errors}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    touched={touched}
                    colourGroups={colourGroups}
                    setFieldValue={setFieldValue}
                    countries={countries}
                  />
                </Box>

                <Flex px={6} pb={6} justifyContent="flex-end">
                  {renderSaveButton}
                </Flex>
              </Flex>
            </form>
          </Flex>
        );
      }}
    </Formik>
  );
};

export default SettingsForm;
